import React from 'react'
import { Box, Container, Typography, Divider, CircularProgress } from '@mui/material'
import { useLocale } from 'next-intl'

import { useGetHomepageUSPQuery } from '@/src/api/rtkQuery/homepageUSPApi'

interface HomeHighlightsProps {
    locale: string
    maxWidth?: 'lg' | 'xl' | false
    isMobile?: boolean
    domain?: string
}

const HomeHighlights: React.FC<HomeHighlightsProps> = ({locale, maxWidth = 'xl', isMobile = false, domain = 'ALL' }) => {
    const isArabic = locale?.startsWith('ar')
    const uspLocale = isArabic ? 'ar' : 'en'
    
    const { data, isLoading, isError } = useGetHomepageUSPQuery(uspLocale)

    if (isLoading) {
        return (
            <Box sx={{ bgcolor: 'grey.50', py: { xs: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress size={40} />
            </Box>
        )
    }

    if (isError || !data) {
        return null
    }

    const renderSection = (title: string, content: React.ReactNode, key: string) => (
        <Box key={key} sx={{ mb: 3 }}>
            <Typography
                component="h2"
                variant="h5"
                sx={{
                    fontWeight: 700,
                    mb: 1.5,
                    color: 'text.primary',
                    fontSize: { xs: '1.125rem', md: '1.25rem' }
                }}
            >
                {title}
            </Typography>
            <Box>{content}</Box>
            <Divider sx={{ mt: 2 }} />
        </Box>
    )

    return (
        <Box sx={{ bgcolor: 'grey.50', py: { xs: 3, md: 4 } }}>
            <Container maxWidth={maxWidth} sx={{ px: { xs: 2, sm: 3 } }}>
                {/* Hero Section */}
                {renderSection(
                    data.hero.title,
                    <Box>
                        <Typography
                            component="h3"
                            variant="body1"
                            sx={{
                                mb: 0.75,
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: { xs: '0.875rem', md: '1rem' }
                            }}
                        >
                            {data.hero.subtitle}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                fontSize: { xs: '0.8125rem', md: '0.875rem' }
                            }}
                        >
                            {data.hero.description}
                        </Typography>
                    </Box>,
                    'hero'
                )}

                {/* Why Book Section */}
                {renderSection(
                    data.whyBook.title,
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                fontSize: { xs: '0.8125rem', md: '0.875rem' }
                            }}
                        >
                            {data.whyBook.description}
                        </Typography>
                        <Box component="ul" sx={{ listStyle: 'disc', pl: { xs: 2.5, md: 3 }, m: 0 }}>
                            <Box component="li" sx={{ mb: 1.5 }}>
                                <Typography
                                    component="h3"
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        mb: 0.5,
                                        fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                    }}
                                >
                                    {data.whyBook.flightFares}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.5,
                                        fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                    }}
                                >
                                    {data.whyBook.flightFaresDescription}
                                </Typography>
                            </Box>
                            <Box component="li" sx={{ mb: 0 }}>
                                <Typography
                                    component="h3"
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        mb: 0.5,
                                        fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                    }}
                                >
                                    {data.whyBook.betterRates}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.5,
                                        fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                    }}
                                >
                                    {data.whyBook.betterRatesDescription}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>,
                    'whyBook'
                )}

                {/* Services Section */}
                {renderSection(
                    'Services',
                    <Box component="ul" sx={{ listStyle: 'disc', pl: { xs: 2.5, md: 3 }, m: 0 }}>
                        <Box component="li" sx={{ mb: 2 }}>
                            <Typography
                                component="h3"
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    mb: 0.5,
                                    fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                }}
                            >
                                {data.travelPlus.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    lineHeight: 1.5,
                                    fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                }}
                            >
                                {data.travelPlus.description}
                            </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 2 }}>
                            <Typography
                                component="h3"
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    mb: 0.5,
                                    fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                }}
                            >
                                {data.stayOnTrack.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    lineHeight: 1.5,
                                    fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                }}
                            >
                                {data.stayOnTrack.description}
                            </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 2 }}>
                            <Typography
                                component="h3"
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    mb: 0.5,
                                    fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                }}
                            >
                                {data.flexifly.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    lineHeight: 1.5,
                                    fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                }}
                            >
                                {data.flexifly.description}
                            </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0 }}>
                            <Typography
                                component="h3"
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    mb: 0.5,
                                    fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                }}
                            >
                                {data.ezcancel.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    lineHeight: 1.5,
                                    fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                }}
                            >
                                {data.ezcancel.description}
                            </Typography>
                        </Box>
                    </Box>,
                    'services'
                )}

                {/* Payment Options */}
                {renderSection(
                    data.flexiblePayment.title,
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 1.5,
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                fontSize: { xs: '0.8125rem', md: '0.875rem' }
                            }}
                        >
                            {data.flexiblePayment.description}
                        </Typography>
                        <Typography
                            component="h3"
                            variant="body1"
                            sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                                mb: 0.5,
                                fontSize: { xs: '0.875rem', md: '0.9375rem' }
                            }}
                        >
                            {data.bookNowPayLater.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.5,
                                fontSize: { xs: '0.75rem', md: '0.8125rem' }
                            }}
                        >
                            {data.bookNowPayLater.description}
                        </Typography>
                    </Box>,
                    'payment'
                )}

                {/* Customer Support */}
                {renderSection(
                    data.customerSupport.title,
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: { xs: '0.8125rem', md: '0.875rem' } }}
                    >
                        {data.customerSupport.description}
                    </Typography>,
                    'support'
                )}

                {/* Discover Saudi Arabia */}
                {renderSection(
                    data.discoverSaudiArabia.sectionTitle,
                    <Box>
                        {data.discoverSaudiArabia.cities.map((city, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Typography
                                    component="h3"
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        mb: 0.75,
                                        fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                    }}
                                >
                                    {city.name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.6,
                                        fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                    }}
                                >
                                    {city.description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>,
                    'discover'
                )}

                {/* Trust & Safety */}
                {renderSection(
                    data.trustAndSafety.title,
                    <Box>
                        <Box sx={{ mb: 2 }}>
                            <Typography
                                component="h3"
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    mb: 0.75,
                                    fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                }}
                            >
                                {data.trustAndSafety.securePayments.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    lineHeight: 1.6,
                                    fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                }}
                            >
                                {data.trustAndSafety.securePayments.description}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                component="h3"
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    mb: 0.75,
                                    fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                }}
                            >
                                {data.trustAndSafety.refundPolicy.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    lineHeight: 1.6,
                                    fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                }}
                            >
                                {data.trustAndSafety.refundPolicy.description}
                            </Typography>
                        </Box>
                    </Box>,
                    'trust'
                )}

                {/* FAQs */}
                {renderSection(
                    data.faqs.title,
                    <Box>
                        {data.faqs.questions.map((faq, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Typography
                                    component="h3"
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        mb: 0.5,
                                        fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                    }}
                                >
                                    {faq.question}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.5,
                                        fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                    }}
                                >
                                    {faq.answer}
                                </Typography>
                            </Box>
                        ))}
                    </Box>,
                    'faqs'
                )}
            </Container>
        </Box>
    )
}

export default HomeHighlights
